import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';

/**
 * Customer Spending Trend Chart Component
 * Displays customer purchase trends over time
 *
 * @param {Array} data - Array of spending data objects with date and amount
 * @param {string} chartType - 'line' or 'bar' chart type
 */
const CustomerSpendingChart = ({ data = [], chartType = 'line' }) => {
    const formatCurrency = (value) => {
        return `₦${value.toLocaleString()}`;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            month: 'short',
            year: 'numeric'
        });
    };

    // If no data, show empty state
    if (!data || data.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-6 h-6 text-accent-gold" />
                    <h3 className="text-lg font-bold text-primary-black">Spending Trend</h3>
                </div>
                <div className="h-64 flex items-center justify-center text-gray-500">
                    <p>No spending data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-accent-gold" />
                    <h3 className="text-lg font-bold text-primary-black">Spending Trend</h3>
                </div>
                <div className="text-sm text-gray-600">
                    Last {data.length} {data.length === 1 ? 'period' : 'periods'}
                </div>
            </div>

            <ResponsiveContainer width="100%" height={300}>
                {chartType === 'bar' ? (
                    <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <YAxis
                            tickFormatter={formatCurrency}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <Tooltip
                            formatter={(value) => [formatCurrency(value), 'Amount']}
                            labelFormatter={formatDate}
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px'
                            }}
                        />
                        <Legend />
                        <Bar
                            dataKey="amount"
                            fill="#D4AF37"
                            name="Spending"
                            radius={[8, 8, 0, 0]}
                        />
                    </BarChart>
                ) : (
                    <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                            dataKey="date"
                            tickFormatter={formatDate}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <YAxis
                            tickFormatter={formatCurrency}
                            tick={{ fontSize: 12 }}
                            stroke="#666"
                        />
                        <Tooltip
                            formatter={(value) => [formatCurrency(value), 'Amount']}
                            labelFormatter={formatDate}
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: '8px'
                            }}
                        />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="amount"
                            stroke="#D4AF37"
                            strokeWidth={3}
                            name="Spending"
                            dot={{ fill: '#D4AF37', r: 5 }}
                            activeDot={{ r: 7 }}
                        />
                    </LineChart>
                )}
            </ResponsiveContainer>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t">
                <div className="text-center">
                    <p className="text-sm text-gray-500">Total</p>
                    <p className="text-lg font-bold text-primary-black">
                        {formatCurrency(data.reduce((sum, item) => sum + item.amount, 0))}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-sm text-gray-500">Average</p>
                    <p className="text-lg font-bold text-blue-600">
                        {formatCurrency(data.reduce((sum, item) => sum + item.amount, 0) / data.length)}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-sm text-gray-500">Highest</p>
                    <p className="text-lg font-bold text-green-600">
                        {formatCurrency(data && data.length > 0 ? Math.max(...data.map(item => item.amount)) : 0)}
                    </p>
                </div>
                <div className="text-center">
                    <p className="text-sm text-gray-500">Lowest</p>
                    <p className="text-lg font-bold text-orange-600">
                        {formatCurrency(data && data.length > 0 ? Math.min(...data.map(item => item.amount)) : 0)}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CustomerSpendingChart;
